import { useState, useEffect } from 'react'; // Added useEffect
import { Loader2, PlusCircle, CreditCard, Package, Tv, Calendar, RefreshCw, DollarSign, CheckCircle, ArrowLeft } from 'lucide-react'; // Added CreditCard
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query'; // Added useQuery, useMutation
import { useNavigate } from 'react-router-dom'; // Added for navigation
import {
  useUserSubscriptions,
  usePlans,
  useBillingPeriods,
  useStripeEnabled,
  Subscription,
  Plan,
  BillingPeriod, // Assuming this is exported from subscriptionService
  listPaymentMethods, // To fetch payment methods for selection
  processSubscriptionPayment,
  getUserChannel
} from '../services/subscriptionService'; // Assuming services are here

// Import child components
import SubscriptionCreation from '../components/subscription/SubscriptionCreation';
import SubscriptionManagement from '../components/subscription/SubscriptionManagement';
import SubscriptionCard from '../components/subscription/SubscriptionCard';
import PaymentMethodForm from '../components/subscription/PaymentMethodForm'; // Added
import PaymentMethodsList from '../components/subscription/PaymentMethodsList'; // Added
import { isAxiosError } from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

// Define the type for payment request details, matching PaymentForm's expected prop
interface PaymentRequestDetails {
  selectedPlanId: number;
  selectedBillingPeriodId: number;
  selectedChannelId: string; // Already present, ensure it's used
  channelName?: string;
  totalPrice: number;
  initialAutoRenewal: boolean; // Already present, ensure it's used
  currentPlanDetails: Plan;
  currentBillingPeriod: BillingPeriod;
}

export default function SubscriptionPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate(); // Added for navigation

  const [paymentStep, setPaymentStep] = useState<'selectingPlan' | 'selectingCard' | 'showingSummary' | 'processing'>('selectingPlan');
  const [paymentRequestDetails, setPaymentRequestDetails] = useState<PaymentRequestDetails | null>(null);
  const [currentManagedSubscription, setCurrentManagedSubscription] = useState<Subscription | null>(null);
  const [isCreatingNewSubscription, setIsCreatingNewSubscription] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [isPaying, setIsPaying] = useState<boolean>(false);

  const { isLoading: isLoadingChannels} = useQuery({
    queryKey: ['userChannels'],
    queryFn: getUserChannel
  })
  const { data: subscriptionsData, isLoading: isLoadingSubscriptions } = useUserSubscriptions();
  const { isLoading: isLoadingPlans } = usePlans();
  const { isLoading: isLoadingBillingPeriods } = useBillingPeriods();
  const { data: stripeEnabledData } = useStripeEnabled();
  const stripeEnabled = stripeEnabledData ?? true;
  
  const { data: paymentMethods, isLoading: isLoadingPaymentMethods } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: listPaymentMethods,
  });

  useEffect(() => {
    if (paymentMethods && paymentMethods.length > 0) {
        const defaultPm = paymentMethods.find(pm => pm.isDefault);
        if (defaultPm) {
            setSelectedPaymentMethodId(defaultPm.id);
        } else if (paymentMethods[0]) {
            setSelectedPaymentMethodId(paymentMethods[0].id);
        }
    }
  }, [paymentMethods]);

  const { mutate: subscriptionPaymentMutate} = useMutation({
    mutationFn: processSubscriptionPayment,
    onSuccess: (data) => {
      if(data) {
        toast.success(t('paymentSuccessful', { paymentIntentId: data.paymentIntentId, message: data.message }));
        queryClient.invalidateQueries({ queryKey: ['userSubscriptions'] });
        queryClient.invalidateQueries({ queryKey: ['userChannels'] });
        setPaymentStep('selectingPlan');
        setPaymentRequestDetails(null);
        setSelectedPaymentMethodId(paymentMethods && paymentMethods.length > 0 ? (paymentMethods.find(pm => pm.isDefault) || paymentMethods[0])?.id || null : null);
        setIsCreatingNewSubscription(false);
      }
    },
    onError: (error: Error) => {
        let errorMessage = t('paymentFailedTryAgain');
        if(isAxiosError(error)) {
          errorMessage = error.response?.data?.message
        } else if(error.message){
          errorMessage = error.message
        }
        
        toast.error(t('paymentFailed', { errorMessage }));
        setPaymentStep('showingSummary');
    },
    onSettled: () => {
        setIsPaying(false);
    }
 });

  const activeSubscriptions = subscriptionsData;

  const handlePaymentMethodAdded = () => {
    queryClient.invalidateQueries({ queryKey: ['paymentMethods'] });
    toast.success(t('newPaymentMethodAddedListRefreshed'));
  };

  const handlePaymentMethodError = (error: Error) => {
     toast.error(t('errorAddingPaymentMethod', { error: error.message }));
  };

  const handleContinueToPayment = (details: PaymentRequestDetails) => {
    setPaymentRequestDetails(details);
    setPaymentStep('selectingCard');
    setIsCreatingNewSubscription(false);
  };

  const handleManageSubscription = (subscriptionId: string) => {
    const subToManage = activeSubscriptions?.find(sub => sub.id === subscriptionId);
    if (subToManage) {
      setCurrentManagedSubscription(subToManage);
      // setShowPaymentView(false); // This state is removed, handled by paymentStep and currentManagedSubscription
    }
  };

  const handleAddNewSubscription = () => {
    setCurrentManagedSubscription(null);
    setPaymentRequestDetails(null);
    setIsCreatingNewSubscription(true);
    setPaymentStep('selectingPlan');
  };

  const handleCancelSubscriptionCreation = () => {
    setIsCreatingNewSubscription(false);
    setPaymentStep('selectingPlan');
    setPaymentRequestDetails(null);
  };

  const renderManualSubscriptionNotice = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-100 flex items-center justify-center px-4">
      <div className="bg-white shadow-xl rounded-lg p-8 max-w-md text-center">
        <p className="text-lg text-gray-700">{t('manualSubscriptionManagementMessage')}</p>
      </div>
    </div>
  );

  const handleSubscriptionUpdated = async () => {
    await queryClient.refetchQueries({ queryKey: ['userSubscriptions'] });
    if(currentManagedSubscription){
        const updatedData = queryClient.getQueryData<Subscription[]>(['userSubscriptions']);
        const updatedSub = updatedData?.find(s => s.id === currentManagedSubscription.id);
        if(updatedSub) setCurrentManagedSubscription(updatedSub);
        else setCurrentManagedSubscription(null);
    }
  }

  if (isLoadingChannels || isLoadingSubscriptions || isLoadingPlans || isLoadingBillingPeriods) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
        <p className="ml-4 text-lg text-gray-700">{t('loadingSubscriptionDetails')}</p>
      </div>
    );
  }

  if (currentManagedSubscription) {
    return (
      <SubscriptionManagement
        key={currentManagedSubscription.id}
        activeSubscription={currentManagedSubscription}
        onSubscriptionUpdate={handleSubscriptionUpdated}
        onBackToList={() => {
          setCurrentManagedSubscription(null);
          setPaymentStep('selectingPlan');
        }}
      />
    );
  }

  if (isCreatingNewSubscription || paymentRequestDetails) {
    if (paymentStep === 'selectingPlan') {
      if (!stripeEnabled) {
        return renderManualSubscriptionNotice();
      }
      return (
        <SubscriptionCreation
          onContinueToPayment={handleContinueToPayment}
          onCancelCreation={handleCancelSubscriptionCreation}
        />
      );
    }

    if (paymentStep === 'selectingCard' && paymentRequestDetails) {
      return (
        <div className="max-w-2xl mx-auto py-10 px-4">
          <h2 className="text-2xl font-semibold mb-6 text-gray-800">{t('selectPaymentMethodTitle')}</h2>
          <div className="bg-white shadow-xl rounded-lg p-6 mb-6">
            <PaymentMethodsList
              paymentMethods={paymentMethods || []}
              isLoading={isLoadingPaymentMethods}
              selectedPaymentMethodId={selectedPaymentMethodId}
              onSelectPaymentMethod={setSelectedPaymentMethodId}
            />
          </div>
          <div className="bg-white shadow-xl rounded-lg p-6">
            <h3 className="text-xl font-medium mb-4 text-gray-700">{t('addNewCardTitle')}</h3>
            <PaymentMethodForm
              onSuccess={() => {
                handlePaymentMethodAdded();
              }}
              onError={handlePaymentMethodError}
            />
          </div>
          <div className="mt-8 flex justify-between">
            <button
              onClick={() => {
                setPaymentStep('selectingPlan');
                setIsCreatingNewSubscription(true);
              }}
              className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('backToPlanSelection')}
            </button>
            <button
              onClick={() => {
                if (!selectedPaymentMethodId) {
                  toast.error(t('pleaseSelectPaymentMethodError'));
                  return;
                }
                setPaymentStep('showingSummary');
              }}
              disabled={!selectedPaymentMethodId || isLoadingPaymentMethods}
              className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isLoadingPaymentMethods ? <Loader2 className="w-4 h-4 mr-2 animate-spin inline" /> : null}
              {t('continueToSummary')}
            </button>
          </div>
        </div>
      );
    }

    if (paymentStep === 'showingSummary' && paymentRequestDetails && selectedPaymentMethodId) {
      const selectedMethodDetails = paymentMethods?.find(pm => pm.id === selectedPaymentMethodId);
      console.log(paymentRequestDetails);
      
      return (
        <div className="max-w-4xl mx-auto py-8 px-4">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-4xl font-bold text-gray-900 mb-3">{t('reviewYourSubscription')}</h1>
            <p className="text-lg text-gray-600">{t('reviewSubscriptionDescription')}</p>
          </div>

          <div className="grid gap-8 lg:grid-cols-3">
            {/* Main Summary Section */}
            <div className="lg:col-span-2">
              <div className="bg-white shadow-2xl rounded-2xl border border-gray-100 overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-8 py-6 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-100 rounded-full">
                      <Package className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{t('subscriptionDetails')}</h2>
                      <p className="text-gray-600">{t('reviewSelectedPlan')}</p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8 space-y-6">
                  {/* Plan Information */}
                  <div className="group hover:shadow-md transition-all duration-200 rounded-xl border-2 border-blue-100 bg-gradient-to-r from-blue-50 to-blue-25 p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-100 rounded-full group-hover:bg-blue-200 transition-colors">
                          <Package className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{t('plan')}</h3>
                          <p className="text-sm text-gray-600">{t('selectedSubscriptionTier')}</p>
                        </div>
                      </div>
                      <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
                        {paymentRequestDetails.currentPlanDetails.name}
                      </span>
                    </div>
                  </div>

                  {/* Channel Information */}
                  <div className="group hover:shadow-md transition-all duration-200 rounded-xl border-2 border-purple-100 bg-gradient-to-r from-purple-50 to-purple-25 p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-purple-100 rounded-full group-hover:bg-purple-200 transition-colors">
                          <Tv className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{t('channel')}</h3>
                          <p className="text-sm text-gray-600">{t('yourStreamingService')}</p>
                        </div>
                      </div>
                      <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-purple-100 text-purple-800 border border-purple-200">
                        {paymentRequestDetails.channelName || paymentRequestDetails.selectedChannelId}
                      </span>
                    </div>
                  </div>

                  {/* Billing Period */}
                  <div className="group hover:shadow-md transition-all duration-200 rounded-xl border-2 border-green-100 bg-gradient-to-r from-green-50 to-green-25 p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-green-100 rounded-full group-hover:bg-green-200 transition-colors">
                          <Calendar className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{t('billingPeriod')}</h3>
                          <p className="text-sm text-gray-600">{t('howOftenCharged')}</p>
                        </div>
                      </div>
                      <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                        {paymentRequestDetails.currentBillingPeriod.label}
                      </span>
                    </div>
                  </div>

                  {/* Auto-renewal */}
                  <div className="group hover:shadow-md transition-all duration-200 rounded-xl border-2 border-orange-100 bg-gradient-to-r from-orange-50 to-orange-25 p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-orange-100 rounded-full group-hover:bg-orange-200 transition-colors">
                          <RefreshCw className="h-5 w-5 text-orange-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{t('autoRenewal')}</h3>
                          <p className="text-sm text-gray-600">{t('automaticSubscriptionRenewal')}</p>
                        </div>
                      </div>
                      <span
                        className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border ${
                          paymentRequestDetails.initialAutoRenewal
                            ? "bg-green-100 text-green-800 border-green-200"
                            : "bg-gray-100 text-gray-800 border-gray-200"
                        }`}
                      >
                        {paymentRequestDetails.initialAutoRenewal ? t('enabledStatus') : t('disabledStatus')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Summary Sidebar */}
            <div className="space-y-6">
              {/* Payment Method Card */}
              <div className="bg-white shadow-xl rounded-2xl border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <CreditCard className="h-5 w-5 text-gray-600" />
                    <h3 className="text-lg font-semibold text-gray-900">{t('paymentMethod')}</h3>
                  </div>
                </div>
                <div className="p-6">
                  {selectedMethodDetails && (
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors">
                      <div className="p-3 bg-white rounded-full shadow-sm">
                        <CreditCard className="h-5 w-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{selectedMethodDetails.cardBrand}</p>
                        <p className="text-sm text-gray-600">•••• •••• •••• {selectedMethodDetails.last4}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Price Summary Card */}
              <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 shadow-xl rounded-2xl border border-green-200 overflow-hidden">
                <div className="bg-gradient-to-r from-green-100 to-emerald-100 px-6 py-4 border-b border-green-200">
                  <div className="flex items-center gap-3">
                    <DollarSign className="h-5 w-5 text-green-600" />
                    <h3 className="text-lg font-semibold text-green-900">{t('totalAmount')}</h3>
                  </div>
                </div>
                <div className="p-8 text-center">
                  <div className="text-4xl font-bold text-green-600 mb-2">
                    ${paymentRequestDetails.totalPrice / 100}
                  </div>
                  <p className="text-sm text-green-700 font-medium">
                    {t('billed')} {paymentRequestDetails.currentBillingPeriod.label.toLowerCase()}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-4">
                <button
                  className={`w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl text-lg font-semibold transition-all duration-200 shadow-lg ${
                    isPaying
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-green-600 hover:bg-green-700 hover:shadow-xl transform hover:-translate-y-0.5"
                  } text-white`}
                  disabled={isPaying}
                  onClick={() => {
                    if (!paymentRequestDetails || !selectedPaymentMethodId) {
                        toast.error(t('missingPaymentDetails'));
                        return;
                    }
                    setPaymentStep('processing');
                    setIsPaying(true);
                    subscriptionPaymentMutate({
                      items: [{
                        id: paymentRequestDetails.currentPlanDetails.id.toString(),
                        quantity: 1,
                        price: paymentRequestDetails.totalPrice
                      }],
                      selectedPaymentMethodId: selectedPaymentMethodId,
                      channelId: paymentRequestDetails.selectedChannelId,
                      autoRenewal: paymentRequestDetails.initialAutoRenewal,
                    });
                  }}
                >
                  {isPaying ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('processing')}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      {t('confirmAndPay')}
                    </>
                  )}
                </button>

                <button
                  className={`w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl text-lg font-semibold transition-all duration-200 border-2 ${
                    isPaying
                      ? "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:shadow-md"
                  }`}
                  disabled={isPaying}
                  onClick={() => setPaymentStep('selectingCard')}
                >
                  <ArrowLeft className="w-5 h-5" />
                  {t('backToPaymentMethod')}
                </button>
              </div>


              {/* Security Badge */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-blue-800">{t('securePayment')}</span>
                </div>
                <p className="text-xs text-blue-600">{t('paymentInfoEncrypted')}</p>
              </div>
            </div>
          </div>

          {/* Processing State Overlay */}
          {isPaying && (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
              <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm mx-4 border border-gray-200">
                <div className="text-center space-y-6">
                  <div className="flex justify-center">
                    <div className="p-4 bg-blue-100 rounded-full">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{t('processingPayment')}</h3>
                    <p className="text-gray-600">{t('processingPaymentDescription')}</p>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: "60%" }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )
    }

     if (paymentStep === 'processing') {
        return (
            <div className="flex flex-col justify-center items-center min-h-screen">
                <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
                <p className="ml-4 text-lg text-gray-700 mt-4">{t('processingYourPayment')}</p>
                <p className="text-sm text-gray-500">{t('doNotRefresh')}</p>
            </div>
        );
    }
  }

  if (activeSubscriptions && activeSubscriptions.length > 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-100 py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-10 pb-4 border-b border-gray-300">
            <h3 className="text-3xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-0">{t('subscriptions')}</h3>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3"> {/* Button group */}
              {stripeEnabled && (
                <button
                  onClick={handleAddNewSubscription}
                  className="flex items-center justify-center py-2 px-5 bg-green-500 text-white rounded-lg font-semibold shadow-md hover:bg-green-600 transition-colors duration-150 ease-in-out"
                >
                  <PlusCircle className="w-5 h-5 mr-2" /> {t('addNewSubscription')}
                </button>
              )}
              {stripeEnabled && (
                <button
                  onClick={() => navigate('/manage-payments')}
                  className="flex items-center justify-center py-2 px-5 bg-blue-600 text-white rounded-lg font-semibold shadow-md hover:bg-blue-700 transition-colors duration-150 ease-in-out"
                >
                  <CreditCard className="w-5 h-5 mr-2" /> {t('managePaymentMethods')}
                </button>
              )}
            </div>
          </div>
          <div className="space-y-6">
            {activeSubscriptions.map(sub => (
              <SubscriptionCard
                key={sub.id}
                subscription={sub}
                onEdit={() => handleManageSubscription(sub.id)}
              />
            ))}
          </div>
          {/* Removed the old payment methods section from here */}
        </div>
      </div>
    );
  }

  if (!stripeEnabled) {
    return renderManualSubscriptionNotice();
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-100">
      <SubscriptionCreation
        onContinueToPayment={handleContinueToPayment}
        onCancelCreation={() => { /* No explicit cancel if this is the only view */ }}
        paymentMethods={paymentMethods || []}
        isLoadingPaymentMethods={isLoadingPaymentMethods}
        selectedPaymentMethodId={selectedPaymentMethodId}
        setSelectedPaymentMethodId={setSelectedPaymentMethodId}
        isProcessingPayment={isPaying}
      />
    </div>
  );
}